import { useState, useEffect, useRef } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { DealerManager } from '@/utils/dealerManager';

/**
 * Custom hook to get dealer-specific welcome message with optimized caching
 */
export const useDealerWelcomeMessage = () => {
  const { language } = useLanguage();
  const [welcomeMessage, setWelcomeMessage] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const hasFetchedRef = useRef(false);
  const currentLanguageRef = useRef(language);

  useEffect(() => {
    const fetchWelcomeMessage = async () => {
      try {
        // Only show loading on initial fetch or language change
        if (!hasFetchedRef.current || currentLanguageRef.current !== language) {
          setIsLoading(true);
          currentLanguageRef.current = language;
        }
        
        // Force clear cache once to get fresh data with new welcome message fields
        if (!hasFetchedRef.current) {
          DealerManager.clearCache();
        }
        
        const message = await DealerManager.getWelcomeMessage(language);
        setWelcomeMessage(message);
        hasFetchedRef.current = true;
      } catch (error) {
        console.error('Failed to fetch welcome message:', error);
        // Fallback to default messages
        const defaultMessages = {
          fr: 'Bonjour, bienvenue chez notre concessionnaire! Je suis votre chatbot IA. Demandez-moi n\'importe quoi!',
          en: 'Good evening! Welcome to our dealership. You can ask me anything!'
        };
        setWelcomeMessage(defaultMessages[language]);
        hasFetchedRef.current = true;
      } finally {
        setIsLoading(false);
      }
    };

    fetchWelcomeMessage();
  }, [language]);

  return { welcomeMessage, isLoading };
};